import React, { FC } from "react";
import {
    FormWidgetDropdownTheme,
    FormWidgetFontTheme,
    FormWidgetTextTheme,
    FormWidgetTheme,
    FormWidgetCardTheme,
    FormWidgetCheckboxTheme,
    FormWidgetChipsTheme,
    FormWidgetDateTheme,
    FormWidgetSideButtonTheme,
    FormWidgetExternalWidgetTheme,
} from "@xapp/stentor-form-widget-channel";

import { err } from "../../utils";

export interface WidgetStylesheetProps {
    readonly theme?: FormWidgetTheme;
}

/**
 * Injects the styles
 *
 * @param props
 * @returns
 */
export const WidgetStylesheet: FC<WidgetStylesheetProps> = (props) => {
    const { theme } = props;

    // Load external font if specified
    React.useEffect(() => {
        const currentFontLink = theme?.font?.link;

        if (currentFontLink) {
            loadExternalFont(currentFontLink);
        }

        // Cleanup function to remove font link when component unmounts or font changes
        return () => {
            if (currentFontLink) {
                const linkId = "xapp-form-widget-font";
                const existingLink = document.getElementById(linkId);
                // Only remove if it's the font we loaded in this effect
                if (existingLink && existingLink.getAttribute("href") === currentFontLink) {
                    existingLink.remove();
                }
            }
        };
    }, [theme?.font?.link]);

    // Apply the theme as CSS custom properties on :root via the CSSOM instead of
    // injecting a <style> tag: setProperty() is not governed by CSP style-src, so
    // the widget themes correctly under a strict policy (style-src 'self', no
    // 'unsafe-inline') without needing a nonce.
    const entriesKey = JSON.stringify(buildStyleVariables(theme));
    React.useLayoutEffect(() => {
        const entries = JSON.parse(entriesKey) as Array<[string, string]>;
        applyThemeVariables(entries);
        return () => removeThemeVariables(entries);
    }, [entriesKey]);

    return null;
};

/**
 * Loads an external font by injecting a link tag into the document head
 * Automatically adds font-display=swap optimization for Google Fonts
 */
export function loadExternalFont(fontUrl: string): void {
    // Validate URL format and security
    try {
        const url = new URL(fontUrl);
        if (url.protocol !== "https:") {
            console.warn("Only HTTPS font URLs are allowed:", fontUrl);
            return;
        }

        // Optional: Restrict to trusted font providers
        const trustedDomains = [
            "fonts.googleapis.com",
            "fonts.gstatic.com",
            "use.typekit.net",
            "use.fontawesome.com",
            "cdn.jsdelivr.net",
            "unpkg.com",
            "cdnjs.cloudflare.com",
        ];

        if (!trustedDomains.includes(url.hostname)) {
            console.warn("Font URL from untrusted domain:", url.hostname);
            // Still proceed but warn - in production you might want to block this
        }

        // Add font-display=swap for Google Fonts if not already present
        if (url.hostname === "fonts.googleapis.com" && !url.searchParams.has("display")) {
            url.searchParams.set("display", "swap");
            fontUrl = url.toString();
        }
    } catch (error) {
        console.error("Invalid font URL:", fontUrl, error);
        return;
    }

    const linkId = "xapp-form-widget-font";

    // Remove existing font link if present
    const existingLink = document.getElementById(linkId);
    if (existingLink) {
        existingLink.remove();
    }

    // Create and inject new font link
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = fontUrl;
    link.crossOrigin = "anonymous"; // Add security attribute
    document.head.appendChild(link);
}

// All instances apply their theme variables to the same target (:root), so a
// per-variable ref-count ensures an unmounting instance only removes a variable
// when no other mounted instance still relies on it.
const appliedVariableCounts = new Map<string, number>();

function applyThemeVariables(entries: Array<[string, string]>): void {
    const root = document.documentElement;
    for (const [key, value] of entries) {
        root.style.setProperty(key, value);
        appliedVariableCounts.set(key, (appliedVariableCounts.get(key) ?? 0) + 1);
    }
}

function removeThemeVariables(entries: Array<[string, string]>): void {
    const root = document.documentElement;
    for (const [key] of entries) {
        const remaining = (appliedVariableCounts.get(key) ?? 1) - 1;
        if (remaining <= 0) {
            appliedVariableCounts.delete(key);
            root.style.removeProperty(key);
        } else {
            appliedVariableCounts.set(key, remaining);
        }
    }
}

/**
 * Generates the [cssVariableName, value] pairs for a form-widget theme, e.g.
 * ["--xapp-form-widget-background-color", "#ffffff"].
 */
function themeVariables(widgetTheme?: FormWidgetTheme): StyleGenerator {
    const theme: FormWidgetTheme = widgetTheme || {};
    return withPrefix(
        "--xapp-form-widget-",
        union(
            single("background-color", theme?.backgroundColor),
            single("header-background-color", theme?.headerBackgroundColor),
            single("header-text-color", theme?.headerTextColor),
            single("primary-button-color", theme?.primaryButtonColor),
            single("primary-button-text-color", theme?.primaryButtonTextColor),
            single("secondary-button-color", theme?.secondaryButtonColor),
            single("secondary-button-text-color", theme?.secondaryButtonTextColor),
            single("border-color", theme?.borderColor),
            // The stylesheet reads two steps of the radius scale and nothing emitted
            // either, so neither could be set at all. One theme value drives both.
            single("border-radius-md", theme?.borderRadius),
            single("border-radius-xl", theme?.borderRadius),
            withPrefix("font-", union(getFontStyle(theme?.font))),
            withPrefix("text-", union(getTextStyle(theme?.text))),
            withPrefix("dropdown-", union(getDropdownStyle(theme?.dropdown))),
            withPrefix("checkbox-", union(getCheckboxStyle(theme?.checkbox))),
            withPrefix("chips-", union(getChipsStyle(theme?.chips))),
            withPrefix("date-", union(getDateStyle(theme?.date))),
            withPrefix("card-", union(getCardStyle(theme?.card))),
            withPrefix("side-button-", union(getSideButtonStyle(theme?.sideButton))),
            withPrefix("external-widget-", union(getExternalWidgetStyle(theme?.externalWidget)))
        )
    );
}

/**
 * Collects the defined theme CSS variables as [name, value] entries, skipping
 * empty values.
 */
export function buildStyleVariables(theme?: FormWidgetTheme): Array<[string, string]> {
    const entries: Array<[string, string]> = [];
    const gen = themeVariables(theme);
    let result = gen.next();
    while (result.done === false) {
        const [key, value] = result.value;
        if (value !== undefined && value !== null && value !== "") {
            entries.push([key, value]);
        }
        result = gen.next();
    }
    return entries;
}

export function buildStyleContent(widgetTheme?: FormWidgetTheme): string {
    const body = buildStyleVariables(widgetTheme)
        .map(([key, value]) => `\t${key}: ${value};`)
        .join("\n");
    return `\n:root {\n${body}\n}\n`;
}

function* getFontStyle(style?: FormWidgetFontTheme): StyleGenerator {
    if (style) {
        yield ["family", style.fontFamily];
    }
}

function* getSideButtonStyle(style?: FormWidgetSideButtonTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["background-color-dark", darken(style.backgroundColor, 10)];
        yield ["color", style.color];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
        yield ["top", style.top];
        yield ["min-length", style.minLength];
    }
}

function* getTextStyle(style?: FormWidgetTextTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["background-color-dark", darken(style.backgroundColor, 3)];
        yield ["color", style.color];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
    }
}

function* getDropdownStyle(style?: FormWidgetDropdownTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["color", style.color];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
    }
}

function* getCheckboxStyle(style?: FormWidgetCheckboxTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["color", style.color];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
    }
}

function* getChipsStyle(style?: FormWidgetChipsTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["background-color-selected", style.backgroundColorSelected];
        yield ["background-color-dark", darken(style.backgroundColor, 20)];
        yield ["color", style.color];
        yield ["color-selected", style.colorSelected];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
    }
}

function* getDateStyle(style?: FormWidgetDateTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["background-color-selected", style.backgroundColorSelected];
        yield ["color", style.color];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
    }
}

function* getCardStyle(style?: FormWidgetCardTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["color", style.color];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
    }
}

function* getExternalWidgetStyle(style?: FormWidgetExternalWidgetTheme): StyleGenerator {
    if (style) {
        yield ["background-color", style.backgroundColor];
        yield ["padding", style.padding];
        yield ["min-height", style.minHeight];
    }
}

declare type StyleGenerator = Generator<[string, string | undefined], void, never>;

// function* empty(): StyleGenerator {}

function* withPrefix(prefix: string, gen: StyleGenerator): StyleGenerator {
    let result = gen.next();
    while (!result.done) {
        yield [`${prefix}${result.value[0]}`, result.value[1]];
        result = gen.next();
    }
}

function* union(...children: StyleGenerator[]): StyleGenerator {
    for (const child of children) {
        let result = child.next();
        while (result.done === false) {
            yield result.value;
            result = child.next();
        }
    }
}

function* single(key: string, value: string | undefined): StyleGenerator {
    yield [key, value];
}

function darken(colorIn: string | undefined, amount: number) {
    if (!colorIn) {
        return colorIn;
    }

    let r: number | undefined, g: number | undefined, b: number | undefined, a: number | undefined;
    let colorOut;

    try {
        // Parse the color format
        if (colorIn.startsWith("#")) {
            // Hex format
            const hexMatch = colorIn.match(/\w\w/g);
            if (hexMatch) {
                [r, g, b] = hexMatch.map((x) => parseInt(x, 16));
            }
        } else if (colorIn.startsWith("rgb")) {
            // RGB or RGBA format
            const rgba = parseRgb(colorIn);
            if (rgba) {
                ({ r, g, b, a } = rgba);
            }
        }

        if (r === undefined || g === undefined || b === undefined) {
            err(`Darken: Unsupported color format: ${colorIn}`);
            return colorIn;
        }

        if (a !== undefined) {
            // Just darkened backdrop?
            if (r === 0 && g === 0 && b === 0) {
                a = a + amount / 100;
            } else {
                // Adjust luminance for RGBA
                const luminance = 0.3 * r + 0.59 * g + 0.11 * b;
                const newLuminance = luminance * (1 - amount / 100);
                const ratio = newLuminance / luminance;
                r = Math.round(r * ratio);
                g = Math.round(g * ratio);
                b = Math.round(b * ratio);
            }

            colorOut = `rgba(${r}, ${g}, ${b}, ${a})`;
        } else {
            // Darken the color for RGB or Hex
            r = Math.max(0, r - amount);
            g = Math.max(0, g - amount);
            b = Math.max(0, b - amount);

            colorOut = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b
                .toString(16)
                .padStart(2, "0")}`;
        }
    } catch (e) {
        err(`Darken: Unsupported color format: ${colorIn} - Error: ${e}`);
    }

    return colorOut;
}

function parseRgb(colorString: string): { r: number; g: number; b: number; a?: number } | null {
    const rgbaRegex = /^rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/;
    const rgbRegex = /^rgb?\((\d+),\s*(\d+),\s*(\d+)\)$/;

    let match: string[] | null;

    // eslint-disable-next-line no-cond-assign
    if ((match = colorString.match(rgbaRegex))) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const a = parseFloat(match[4]);

        return { r, g, b, a };
        // eslint-disable-next-line no-cond-assign
    } else if ((match = colorString.match(rgbRegex))) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);

        return { r, g, b };
    } else {
        return null;
    }
}
